/**
 * DateDivider Component - HYB Shop Chat
 * Elegant date separator for message grouping
 *
 * Design: Subtle gradient lines with centered date text
 */

import { useMemo } from 'react';
import { cn } from '../../../utils/responsive';

interface DateDividerProps {
  /** Date to display */
  date: Date | string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Format date to Thai relative format
 */
function formatRelativeDate(date: Date): string {
  const now = new Date();
  const inputDate = new Date(date);

  // Reset time for comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const inputDay = new Date(
    inputDate.getFullYear(),
    inputDate.getMonth(),
    inputDate.getDate()
  );

  const diffTime = today.getTime() - inputDay.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'วันนี้';
  }

  if (diffDays === 1) {
    return 'เมื่อวาน';
  }

  if (diffDays > 1 && diffDays <= 6) {
    // Within a week - show day name
    const dayNames = [
      'วันอาทิตย์',
      'วันจันทร์',
      'วันอังคาร',
      'วันพุธ',
      'วันพฤหัสบดี',
      'วันศุกร์',
      'วันเสาร์',
    ];
    return dayNames[inputDate.getDay()];
  }

  // Older than a week - show full date in Thai format
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: inputDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  };

  return inputDate.toLocaleDateString('th-TH', options);
}

export function DateDivider({ date, className }: DateDividerProps) {
  const formattedDate = useMemo(() => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return formatRelativeDate(dateObj);
  }, [date]);

  return (
    <div
      className={cn(
        'flex items-center gap-4 py-4 select-none',
        className
      )}
      role="separator"
      aria-label={`ข้อความ ${formattedDate}`}
    >
      {/* Left Line */}
      <div
        className={cn(
          'flex-1 h-px',
          'bg-gradient-to-r from-transparent via-stone-200 to-stone-200',
          'dark:via-gray-700 dark:to-gray-700'
        )}
      />

      {/* Date Text */}
      <span
        className={cn(
          'text-xs font-medium',
          'text-stone-500 dark:text-gray-400',
          'bg-stone-50 dark:bg-gray-900',
          'px-3 py-1 rounded-full',
          'border border-stone-200 dark:border-gray-700',
          'shadow-sm'
        )}
      >
        {formattedDate}
      </span>

      {/* Right Line */}
      <div
        className={cn(
          'flex-1 h-px',
          'bg-gradient-to-l from-transparent via-stone-200 to-stone-200',
          'dark:via-gray-700 dark:to-gray-700'
        )}
      />
    </div>
  );
}

export default DateDivider;
